import React, { useEffect, useState } from "react";
import axios from "axios";
import { useStore } from "../../utils/store";
import { useNavigate } from "react-router-dom";
import "./ChallengeDashboard.scss";
import Footer from "../Footer/Footer";
import HiLifeLogo from "../Icons/HiLifeLogo";

function ChallengeDashboard() {
  const navigate = useNavigate();
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [allChallenges, setAllChallenges] = useState([]);

  const [user, setUser, setUserChallenge] = useStore((state) => [
    state.user,
    state.setUser,
    state.setUserChallenge,
  ]);

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        const response = await axios.get(
          `${
            process.env.NODE_ENV === "production"
              ? "api"
              : "http://localhost:5533/api"
          }/challenge`
        );

        const allData = response.data;
        const currentDate = new Date();

        // Get IDs of challenges user has joined (payWall = true)
        const userChallengeIds = user.challenges
          ? user.challenges
              .filter((uc) => uc.payWall)
              .map((uc) => uc.challenge)
          : [];

        // Active/Upcoming challenges (always show for everyone to join)
        const activeOrUpcoming = allData
          .filter((challenge) => {
            const endDateWithGrace = new Date(challenge.endDate);
            endDateWithGrace.setDate(endDateWithGrace.getDate() + 2);
            const hasEndedWithGrace = endDateWithGrace < currentDate;
            const isActive = challenge.active === true;

            return !hasEndedWithGrace && isActive;
          })
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

        // My past challenges (only those user has participated in)
        const myPastChallenges = allData
          .filter((challenge) => {
            const endDate = new Date(challenge.endDate);
            const hasEnded = endDate < currentDate;
            const hasJoined = userChallengeIds.includes(challenge._id);

            // Show past challenges only if user joined
            return hasEnded && hasJoined;
          })
          .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

        // For developers: show all challenges
        const all = allData.sort(
          (a, b) => new Date(b.startDate) - new Date(a.startDate)
        );

        setActiveChallenges(activeOrUpcoming);
        setMyChallenges(myPastChallenges);
        setAllChallenges(all);
      } catch (error) {
        console.error("Error fetching challenges:", error);
      }
    };

    fetchChallenges();
  }, [user.challenges, user.isDeveloper]);

  const handleChallengeClick = async (challengeId, userId, isPaid) => {
    if (isPaid) {
      // Already paid/joined - go to dashboard
      const userChallenge = user.challenges.find(
        (uc) => uc.challenge === challengeId
      );
      setUser({ ...user, challenge: userChallenge });
      setUserChallenge(userChallenge);
      navigate("/dashboard", { state: { challengeId, userId } });
    } else if (user.admin || user.isDeveloper) {
      // Admin/Developer bypass - call admin-join endpoint
      try {
        const response = await axios.post(
          `${
            process.env.NODE_ENV === "production"
              ? "api"
              : "http://localhost:5533/api"
          }/challenge/admin-join`,
          { userId, challengeId }
        );
        if (response.data.success) {
          setUser(response.data.user);
          const userChallenge = response.data.user.challenges.find(
            (uc) => uc.challenge === challengeId
          );
          setUserChallenge(userChallenge);
          navigate("/dashboard", { state: { challengeId, userId } });
        }
      } catch (error) {
        console.error("Admin join error:", error);
      }
    } else {
      // Regular user - go to paywall
      navigate("/paywall", { state: { challengeId, userId } });
    }
  };

  const isPaidChallenge = (challengeId) => {
    if (!user.challenges) return false;
    const userChallenge = user.challenges.find(
      (uc) => uc.challenge === challengeId
    );
    return Boolean(userChallenge?.payWall);
  };

  const getChallengeStatus = (challenge) => {
    const currentDate = new Date();
    const startDate = new Date(challenge.startDate);
    const endDate = new Date(challenge.endDate);

    if (currentDate < startDate) return "upcoming";
    if (currentDate > endDate) return "ended";
    return "active";
  };

  const renderChallengeCard = (challenge, showStatus = false) => {
    const isPaid = isPaidChallenge(challenge._id);
    const status = getChallengeStatus(challenge);

    return (
      <div
        key={challenge._id}
        className={`challenge-card ${isPaid ? "challenge-card--joined" : ""} ${
          status === "ended" && isPaid ? "challenge-card--past" : ""
        }`}
        onClick={() => handleChallengeClick(challenge._id, user._id, isPaid)}
      >
        {challenge.image && (
          <div className="challenge-card__image">
            <img src={challenge.image} alt={challenge.name} />
          </div>
        )}
        <div className="challenge-card__content">
          <h3 className="challenge-card__title">{challenge.name}</h3>
          <p className="challenge-card__date">
            {new Date(challenge.startDate).toLocaleDateString("de-DE")} -{" "}
            {new Date(challenge.endDate).toLocaleDateString("de-DE")}
          </p>
          {showStatus && (
            <span className={`challenge-card__status challenge-card__status--${status}`}>
              {status === "upcoming" && "Bald"}
              {status === "active" && "Aktiv"}
              {status === "ended" && "Beendet"}
            </span>
          )}
          {isPaid ? (
            <button className="challenge-card__btn challenge-card__btn--view">
              ÖFFNEN
            </button>
          ) : (
            <>
              <p className="challenge-card__price">{challenge.price} €</p>
              <button className="challenge-card__btn challenge-card__btn--join">
                {user.admin || user.isDeveloper ? "BEITRETEN" : "JETZT ZAHLEN"}
              </button>
              {!user.admin && !user.isDeveloper && (
                <p className="challenge-card__hint">
                  Gutscheincode im nächsten Schritt
                </p>
              )}
            </>
          )}
        </div>
      </div>
    );
  };


  const hasContent = activeChallenges.length > 0 || myChallenges.length > 0;

  return (
    <main className="challenge-dashboard">
      <div className="challenge-dashboard__header">
        <HiLifeLogo />
      </div>

      {!hasContent && !user.isDeveloper && (
        <p className="challenge-dashboard__empty">
          Aktuell gibt es keine Challenges.
        </p>
      )}

      {/* Active/Upcoming Challenges - always show for joining */}
      {activeChallenges.length > 0 && (
        <section className="challenge-dashboard__section">
          <h2 className="challenge-dashboard__section-title">
            Aktuelle Challenges
          </h2>
          <div className="challenge-dashboard__grid">
            {activeChallenges.map((challenge) =>
              renderChallengeCard(challenge, true)
            )}
          </div>
        </section>
      )}

      {/* Past challenges - only those user has joined */}
      {myChallenges.length > 0 && (
        <section className="challenge-dashboard__section">
          <h2 className="challenge-dashboard__section-title challenge-dashboard__section-title--past">
            Meine vergangenen Challenges
          </h2>
          <div className="challenge-dashboard__grid">
            {myChallenges.map((challenge) =>
              renderChallengeCard(challenge, true)
            )}
          </div>
        </section>
      )}

      {/* Developer view - all challenges */}
      {user.isDeveloper && (
        <section className="challenge-dashboard__section">
          <h2 className="challenge-dashboard__section-title challenge-dashboard__section-title--dev">
            🛠 Developer - Alle Challenges
          </h2>
          <div className="challenge-dashboard__grid">
            {allChallenges.map((challenge) =>
              renderChallengeCard(challenge, true)
            )}
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}

export default ChallengeDashboard;
